import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Modal } from "./Modal";
import { formatDate } from "../lib/api";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

function printId(student, library, photoDataUrl) {
  const win = window.open("", "_blank", "width=720,height=900");
  if (!win) return;
  const brand = escapeHtml(library?.name || "LibraryOS");
  const footer = escapeHtml(library?.footer_text || "Powered by MKS Digital Technology");
  const initial = escapeHtml((student.name || "L").charAt(0).toUpperCase());
  const name = escapeHtml(student.name);
  const studentId = escapeHtml(student.student_id);
  const mobile = escapeHtml(student.mobile || "—");
  const seat = escapeHtml(student.seat_id ? "Assigned" : "—");
  const shift = escapeHtml(student.shift_name || "—");
  const joined = escapeHtml(formatDate(student.joining_date));
  const ending = escapeHtml(formatDate(student.ending_date));
  const status = escapeHtml(student.status || "—");
  const fee = Number(student.monthly_fee || 0);
  const city = escapeHtml(student.city || library?.city || "—");
  const emergency = escapeHtml(student.emergency_contact || student.mobile || "—");
  const address = escapeHtml(library?.address || library?.city || "");
  const avatarHtml = photoDataUrl
    ? `<img src="${photoDataUrl}" alt="${name}" />`
    : `<span>${initial}</span>`;
  const styles = `
    * { box-sizing:border-box; }
    body { margin:0; font-family:'Plus Jakarta Sans','Inter',sans-serif; background:#f1f5f9; padding:40px; color:#0f172a; }
    .cards { display:flex; gap:24px; flex-wrap:wrap; justify-content:center; }
    .card { width:340px; min-height:540px; border-radius:20px; overflow:hidden; box-shadow:0 24px 60px #0f172a24; background:#fff; display:flex; flex-direction:column; }
    .card header { background:linear-gradient(135deg,#1d4ed8,#1e293b); color:#fff; padding:22px 24px; }
    .card header small { font-size:11px; letter-spacing:0.14em; opacity:.75; }
    .card header h1 { font-size:20px; letter-spacing:-0.02em; margin:4px 0 0; }
    .avatar { width:112px; height:112px; border-radius:50%; margin:28px auto 14px; background:linear-gradient(135deg,#dbeafe,#bfdbfe); display:grid; place-items:center; color:#1e3a8a; font-size:48px; font-weight:700; border:4px solid #fff; box-shadow:0 10px 24px #0f172a1a; overflow:hidden; }
    .avatar img { width:100%; height:100%; object-fit:cover; }
    .meta { padding:0 28px; text-align:center; }
    .meta h2 { font-size:19px; letter-spacing:-0.02em; margin:0; }
    .meta small { color:#64748b; font-size:12px; }
    .info { padding:20px 28px 0; display:grid; gap:10px; }
    .info div { display:flex; justify-content:space-between; font-size:12px; }
    .info span { color:#64748b; }
    .info strong { color:#0f172a; font-weight:600; }
    .card footer { margin-top:auto; padding:14px 24px; background:#f8fafc; color:#64748b; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; text-align:center; border-top:1px solid #e2e8f0; }
    .back { background:#0f172a; color:#fff; }
    .back header { background:#0f172a; }
    .back .info { color:#e2e8f0; }
    .back .info span { color:#94a3b8; }
    .back .info strong { color:#fff; }
    .back footer { background:#1e293b; color:#94a3b8; border-color:#334155; }
    @media print { body { background:#fff; padding:20px; } }
  `;
  const front = `
    <div class="card">
      <header>
        <small>${brand.toUpperCase()}</small>
        <h1>Member ID card</h1>
      </header>
      <div class="avatar">${avatarHtml}</div>
      <div class="meta">
        <h2>${name}</h2>
        <small>${studentId}</small>
      </div>
      <div class="info">
        <div><span>Mobile</span><strong>${mobile}</strong></div>
        <div><span>Seat</span><strong>${seat}</strong></div>
        <div><span>Shift</span><strong>${shift}</strong></div>
        <div><span>Joined</span><strong>${joined}</strong></div>
        <div><span>Valid till</span><strong>${ending}</strong></div>
      </div>
      <footer>${footer}</footer>
    </div>`;
  const back = `
    <div class="card back">
      <header>
        <small>${brand.toUpperCase()}</small>
        <h1>Membership details</h1>
      </header>
      <div class="info" style="padding:28px">
        <div><span>Status</span><strong>${status}</strong></div>
        <div><span>Monthly fee</span><strong>₹${fee}</strong></div>
        <div><span>Address</span><strong>${city}</strong></div>
        <div><span>Emergency</span><strong>${emergency}</strong></div>
      </div>
      <div class="info" style="padding:12px 28px">
        <div style="flex-direction:column;align-items:flex-start;gap:4px;font-size:11px"><span>If found, please return to:</span><strong>${brand}</strong><span>${address}</span></div>
      </div>
      <footer>${footer}</footer>
    </div>`;
  win.document.write(`<title>${studentId} · ID card</title><style>${styles}</style><div class="cards">${front}${back}</div><script>window.onload=()=>window.print();<\/script>`);
  win.document.close();
}

export default function IdCardModal({ student, library, photoUrl, close }) {
  const initial = (student.name || "L").charAt(0).toUpperCase();
  const [printDataUrl, setPrintDataUrl] = useState(null);

  useEffect(() => {
    if (!photoUrl) {
      setPrintDataUrl(null);
      return;
    }
    let cancelled = false;
    fetch(photoUrl)
      .then((res) => res.blob())
      .then((blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then((dataUrl) => { if (!cancelled) setPrintDataUrl(dataUrl); })
      .catch(() => { if (!cancelled) setPrintDataUrl(null); });
    return () => { cancelled = true; };
  }, [photoUrl]);

  return (
    <Modal title="Student ID card" eyebrow="PRINT PREVIEW" close={close} testId="id-card-modal">
      <div className="id-card-preview" data-testid="id-card-preview">
        <header>
          <small>{(library?.name || "LibraryOS").toUpperCase()}</small>
          <h3>Member ID card</h3>
        </header>
        <div className="id-avatar">{photoUrl ? <img src={photoUrl} alt={student.name} /> : initial}</div>
        <h4>{student.name}</h4>
        <small>{student.student_id}</small>
        <ul>
          <li><span>Mobile</span><strong>{student.mobile || "—"}</strong></li>
          <li><span>Joined</span><strong>{formatDate(student.joining_date)}</strong></li>
          <li><span>Valid till</span><strong>{formatDate(student.ending_date)}</strong></li>
          <li><span>Seat</span><strong>{student.seat_id ? "Assigned" : "—"}</strong></li>
        </ul>
        <footer>{library?.footer_text || "Powered by MKS Digital Technology"}</footer>
      </div>
      <div className="modal-actions">
        <button type="button" className="button button-outline" onClick={close}>Close</button>
        <button type="button" className="button button-primary" onClick={() => printId(student, library, printDataUrl)} data-testid="print-id-card-confirm"><Printer size={16} /> Print</button>
      </div>
    </Modal>
  );
}
